'use client';

/**
 * Личность пользователя и права.
 *
 * Права — это гранты: роль плюс узел дерева. Грубые роли из токена
 * (`me.roles`) решать ничего не могут: «руководитель» без узла — половина
 * права, и в `users` колонки `role` поэтому не существует.
 *
 * Фронт прячет то, чего человеку нельзя, но **не решает, что ему видно**:
 * область видимости считает база функцией `visible_units`. Два независимых
 * набора правил разъедутся молча — в сторону показа чужих данных.
 */

import React, { createContext, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/lib/api/keys';
import { getMe } from '@/lib/api/endpoints';
import type { GrantRole, Me } from '@/lib/api/types';

const MeContext = createContext<Me | null>(null);

export function MeProvider({ me, children }: { me: Me; children: React.ReactNode }) {
  return <MeContext.Provider value={me}>{children}</MeContext.Provider>;
}

/** Внутри защищённых экранов личность есть всегда — гард без неё не пускает. */
export function useMe(): Me {
  const me = useContext(MeContext);
  if (!me) throw new Error('useMe вызван вне MeProvider — экран не под гардом?');
  return me;
}

/**
 * Запрос личности.
 *
 * Первый вызов нового пользователя заметно медленнее: бэкенд заводит строку
 * и добирает имя с почтой из userinfo Zitadel — в токенах их нет. Это один
 * сетевой вызов, а не сбой.
 */
export function useMeQuery(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: getMe,
    enabled,
    // Гранты не кэшируем дольше сессии: человека переводят между
    // подразделениями, и вчерашняя область видимости уже неверна.
    staleTime: 0,
    gcTime: 0,
  });
}

export function hasRole(me: Me, role: GrantRole): boolean {
  return me.grants.some((grant) => grant.role === role);
}

/** Показывать ли кнопки администрирования. Решает только видимость кнопок. */
export function canAdminister(me: Me): boolean {
  return hasRole(me, 'tenant_admin') || hasRole(me, 'unit_admin');
}

/** Узлы, где человек администратор. Для подсказок в формах, не для фильтрации. */
export function administeredUnitIds(me: Me): string[] {
  return me.grants
    .filter((grant) => grant.role === 'tenant_admin' || grant.role === 'unit_admin')
    .map((grant) => grant.unitId);
}

/** Узел по умолчанию в фильтрах — тот, куда человек принят. */
export function defaultUnitId(me: Me): string | null {
  return me.homeUnitId;
}

/**
 * Самый сильный грант — для подписи под именем.
 *
 * Строка в шапке, а не право: решать по ней ничего нельзя, у человека может
 * быть несколько грантов на разных узлах.
 */
const roleWeight: Record<GrantRole, number> = {
  tenant_admin: 5,
  unit_admin: 4,
  supervisor: 3,
  analyst: 2,
  agent: 1,
};

export function primaryRole(me: Me): GrantRole | null {
  let best: GrantRole | null = null;
  for (const grant of me.grants) {
    if (!best || roleWeight[grant.role] > roleWeight[best]) best = grant.role;
  }
  return best;
}

/**
 * Роли, которыми человек обладает **на конкретном узле или выше**.
 *
 * Грант на регионе действует и на его салонах, поэтому сравнивать один
 * `unitId` с другим недостаточно: нужен путь. Пути живут в дереве
 * подразделений, а не в грантах, — поэтому функция принимает справочник
 * путей отдельно.
 *
 * Это решает только, показывать ли кнопку. Настоящую проверку делает сервер,
 * и ошибиться здесь не страшно: придёт 403.
 */
export function rolesOnUnit(
  me: Me,
  unitPath: string | null | undefined,
  pathOfUnit: (unitId: string) => string | undefined
): Set<GrantRole> {
  const roles = new Set<GrantRole>();
  if (!unitPath) return roles;

  for (const grant of me.grants) {
    const grantPath = pathOfUnit(grant.unitId);
    // Узел звонка внутри поддерева гранта: сам узел или его потомок.
    if (grantPath && (unitPath === grantPath || unitPath.startsWith(`${grantPath}.`))) {
      roles.add(grant.role);
    }
  }
  return roles;
}

/** Комментарий пишут все, кроме оператора: у себя он не разбирающий, а сторона. */
export function canComment(roles: Set<GrantRole>): boolean {
  return (
    roles.has('supervisor') ||
    roles.has('analyst') ||
    roles.has('unit_admin') ||
    roles.has('tenant_admin')
  );
}

/**
 * Оценку правят те же, но без аналитика: правка — управленческое решение с
 * последствиями для показателей человека, а не объяснение.
 */
export function canReviewScore(roles: Set<GrantRole>): boolean {
  return roles.has('supervisor') || roles.has('unit_admin') || roles.has('tenant_admin');
}
